import { useEffect, useId, useRef, useState } from 'react'
import { Check, ListFilter } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CheckFilterOption {
  /** The value written back through `onChange`. */
  value: string
  /** What the row reads, e.g. "LATENCY_HIGH". */
  label: string
  /** How many rows carry this value — a filter with nothing behind it is a trap. */
  count: number
  /** Optional colour class for the label, so it matches the column it filters. */
  className?: string
}

interface CheckFilterMenuProps {
  options: CheckFilterOption[]
  /** Chosen values. **Empty means "everything"**, not "nothing" — see below. */
  selected: string[]
  onChange: (next: string[]) => void
  /** Screen-reader name for the trigger, e.g. "Filter incidents by indicator". */
  label: string
  /** Heading inside the panel, e.g. "Indicator". */
  title: string
}

/**
 * A filter icon that opens a multi-select checklist.
 *
 * Distinct from `FilterDropdown`, which is a single-select listbox for a scope
 * chip: this one narrows a table by any number of values at once and shows the
 * row count behind each, so it can be seen that ticking one leaves four rows
 * before it is ticked.
 *
 * **Empty selection means everything.** Modelling it as "no boxes ticked = no
 * rows" would make the natural first interaction — open the menu, look — blank
 * the table, and it would leave two ways to express "show me all of it" (tick
 * every box, or tick none) that render identically. So an untouched menu is the
 * unfiltered state, and clearing returns to it.
 *
 * Options are expected to be derived from the data on screen rather than from a
 * static list of every possible value, so the menu never offers a filter that
 * matches nothing.
 */
export function CheckFilterMenu({
  options,
  selected,
  onChange,
  label,
  title,
}: CheckFilterMenuProps) {
  const [open, setOpen] = useState(false)
  /**
   * Whether the panel hangs above the trigger instead of below it.
   *
   * Measured rather than fixed, because this menu's usual home is the header of
   * the last table on a page — where there is often no room below, and a panel
   * that opened downward would land off the bottom of the viewport.
   */
  const [dropUp, setDropUp] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const panelId = useId().replace(/:/g, '') + '-panel'

  const active = selected.length > 0

  /** Roughly the panel's tallest form: header plus a capped option list. */
  const PANEL_HEIGHT = 300

  const openMenu = () => {
    const rect = trigger.current?.getBoundingClientRect()
    if (rect) {
      const below = window.innerHeight - rect.bottom
      // Flip only when there is genuinely more room the other way, so a menu
      // near the top of a short page still opens downward as expected.
      setDropUp(below < PANEL_HEIGHT && rect.top > below)
    }
    setOpen(true)
  }

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    )
  }

  // Dismiss on an outside press. Pointerdown rather than click, so dragging out
  // of the panel and releasing doesn't leave it open.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (trigger.current?.contains(target) || panel.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      trigger.current?.focus()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="relative">
      <button
        ref={trigger}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={
          active ? `${label} (${selected.length} applied)` : label
        }
        title={label}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={cn(
          'flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors outline-none',
          'focus-visible:ring-primary/25 focus-visible:ring-2',
          // An active filter has to be visible from across the page — a table
          // showing three of twelve rows with no sign of why is a bug report.
          active
            ? 'text-primary-bright bg-primary/10'
            : open
              ? 'text-fg bg-surface-3'
              : 'text-fg-subtle hover:text-fg',
        )}
      >
        <ListFilter className="size-4" />
        {active && (
          <span className="font-mono text-[11px] font-semibold tabular-nums">
            {selected.length}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panel}
          id={panelId}
          role="group"
          aria-label={title}
          className={cn(
            'border-line-bright bg-surface-2 animate-pop-in absolute right-0 z-30',
            'min-w-[220px] rounded-lg border p-1 shadow-xl shadow-black/40',
            dropUp ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]',
          )}
        >
          <div className="flex items-center justify-between gap-3 px-2.5 py-2">
            <span className="text-fg-subtle font-mono text-[10px] font-semibold tracking-[0.08em] uppercase">
              {title}
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={!active}
              className={cn(
                'font-mono text-[10px] font-semibold tracking-[0.08em] uppercase transition-colors',
                active
                  ? 'text-primary-bright hover:text-primary'
                  : 'text-fg-subtle cursor-not-allowed opacity-60',
              )}
            >
              Clear
            </button>
          </div>

          <ul className="max-h-64 overflow-y-auto">
            {options.map((option) => {
              const checked = selected.includes(option.value)
              return (
                <li key={option.value}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2',
                      'hover:bg-surface-3 font-mono text-[11px] font-semibold tracking-[0.08em]',
                      checked ? 'text-fg' : 'text-fg-muted',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(option.value)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden
                      className={cn(
                        'grid size-3.5 shrink-0 place-items-center rounded-[3px] border',
                        checked ? 'border-primary bg-primary/20' : 'border-line-bright',
                      )}
                    >
                      <Check
                        className={cn(
                          'text-primary-bright size-3',
                          checked ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </span>
                    <span className={cn('flex-1 truncate', option.className)}>{option.label}</span>
                    <span className="text-fg-subtle shrink-0 tabular-nums">{option.count}</span>
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
